/**
 * Coach Schedule — data layer for coach session scheduling.
 * Supabase-backed with AsyncStorage offline fallback.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('COACH_SCHEDULE');

export interface CoachSession {
  id: string;
  coach_id: string;
  client_id: string | null;
  client_name: string | null;
  session_date: string;   // "YYYY-MM-DD"
  start_time: string;     // "HH:MM"
  duration_minutes: number;
  notes: string | null;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  created_at?: string;
  updated_at?: string;
}

// ── Offline queue helpers ──

const OFFLINE_KEY = 'duel:coach_sessions_offline_queue';

interface OfflineOp {
  id: string;
  type: 'insert' | 'update' | 'delete';
  payload: any;
  retryCount: number;
}

async function enqueueOffline(op: Omit<OfflineOp, 'id' | 'retryCount'>): Promise<void> {
  try {
    const raw = await AsyncStorage.getItem(OFFLINE_KEY);
    const queue: OfflineOp[] = raw ? JSON.parse(raw) : [];
    queue.push({
      ...op,
      id: `op-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      retryCount: 0,
    });
    await AsyncStorage.setItem(OFFLINE_KEY, JSON.stringify(queue));
    log.info('Queued offline coach schedule op', { type: op.type });
  } catch (e) {
    log.warn('Failed to enqueue offline op', { error: e });
  }
}

export async function syncOfflineCoachOps(): Promise<{ synced: number; failed: number }> {
  let synced = 0;
  let failed = 0;
  try {
    const raw = await AsyncStorage.getItem(OFFLINE_KEY);
    const queue: OfflineOp[] = raw ? JSON.parse(raw) : [];
    if (queue.length === 0) return { synced: 0, failed: 0 };

    const remaining: OfflineOp[] = [];
    for (const op of queue) {
      try {
        let error: any = null;
        if (op.type === 'insert') {
          ({ error } = await supabase.from('coach_sessions').insert(op.payload));
        } else if (op.type === 'update') {
          ({ error } = await supabase.from('coach_sessions').update(op.payload.updates).eq('id', op.payload.id));
        } else if (op.type === 'delete') {
          ({ error } = await supabase.from('coach_sessions').delete().eq('id', op.payload.id));
        }
        if (error) throw error;
        synced++;
      } catch {
        op.retryCount++;
        if (op.retryCount < 5) remaining.push(op);
        failed++;
      }
    }
    await AsyncStorage.setItem(OFFLINE_KEY, JSON.stringify(remaining));
  } catch {
    // ignore
  }
  return { synced, failed };
}

// ── Local cache for offline reads ──

const CACHE_KEY = (coachId: string) => `duel:coach_sessions_cache:${coachId}`;

async function cacheLocally(coachId: string, sessions: CoachSession[]): Promise<void> {
  try {
    await AsyncStorage.setItem(CACHE_KEY(coachId), JSON.stringify(sessions));
  } catch { /* ignore */ }
}

async function readCache(coachId: string): Promise<CoachSession[]> {
  try {
    const raw = await AsyncStorage.getItem(CACHE_KEY(coachId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ── Public API ──

export async function getSessions(
  coachId: string,
  date?: string,
): Promise<CoachSession[]> {
  try {
    let query = supabase
      .from('coach_sessions')
      .select('*')
      .eq('coach_id', coachId)
      .order('session_date')
      .order('start_time');

    if (date) {
      query = query.eq('session_date', date);
    }

    const { data, error } = await query;
    if (error) throw error;
    const sessions = (data ?? []) as CoachSession[];
    await cacheLocally(coachId, sessions);
    return sessions;
  } catch (e) {
    log.warn('getSessions failed, using cache', { error: e });
    const cached = await readCache(coachId);
    if (date) return cached.filter((s) => s.session_date === date);
    return cached;
  }
}

export async function getSessionsForWeek(
  coachId: string,
  weekStart: Date,
): Promise<CoachSession[]> {
  const startStr = formatDateStr(weekStart);
  const weekEnd = new Date(weekStart.getTime() + 6 * 86400000);
  const endStr = formatDateStr(weekEnd);

  try {
    const { data, error } = await supabase
      .from('coach_sessions')
      .select('*')
      .eq('coach_id', coachId)
      .gte('session_date', startStr)
      .lte('session_date', endStr)
      .order('session_date')
      .order('start_time');

    if (error) throw error;
    return (data ?? []) as CoachSession[];
  } catch (e) {
    log.warn('getSessionsForWeek failed, using cache', { error: e });
    const cached = await readCache(coachId);
    return cached.filter((s) => s.session_date >= startStr && s.session_date <= endStr);
  }
}

export async function addSession(
  session: Omit<CoachSession, 'id'>,
): Promise<CoachSession> {
  try {
    const { data, error } = await supabase
      .from('coach_sessions')
      .insert(session)
      .select()
      .single();

    if (error) throw error;
    return data as CoachSession;
  } catch (e) {
    log.warn('addSession failed, queuing offline', { error: e });
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    await enqueueOffline({ type: 'insert', payload: session });
    return { ...session, id: tempId } as CoachSession;
  }
}

export async function updateSession(
  id: string,
  updates: Partial<CoachSession>,
): Promise<void> {
  try {
    const { error } = await supabase
      .from('coach_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  } catch (e) {
    log.warn('updateSession failed, queuing offline', { error: e });
    await enqueueOffline({ type: 'update', payload: { id, updates } });
  }
}

export async function deleteSession(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('coach_sessions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (e) {
    log.warn('deleteSession failed, queuing offline', { error: e });
    await enqueueOffline({ type: 'delete', payload: { id } });
  }
}

/** Cancel = soft delete via status update */
export async function cancelSession(id: string): Promise<void> {
  await updateSession(id, { status: 'cancelled' });
}

// ── Helpers ──

function formatDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Seed mock sessions (dev only) */
export async function seedMockSessions(coachId: string): Promise<void> {
  const existing = await getSessions(coachId);
  if (existing.length > 0) return;

  const today = new Date();
  const todayStr = formatDateStr(today);
  const tomorrow = new Date(today.getTime() + 86400000);
  const tomorrowStr = formatDateStr(tomorrow);
  const dayAfter = new Date(today.getTime() + 2 * 86400000);
  const dayAfterStr = formatDateStr(dayAfter);

  const mocks: Omit<CoachSession, 'id'>[] = [
    { coach_id: coachId, client_id: 'mock-1', client_name: 'Alex Rivera', session_date: todayStr, start_time: '09:00', duration_minutes: 60, notes: 'Upper body strength', status: 'scheduled' },
    { coach_id: coachId, client_id: 'mock-3', client_name: 'Sam Okafor', session_date: todayStr, start_time: '14:00', duration_minutes: 45, notes: 'Recovery & mobility', status: 'scheduled' },
    { coach_id: coachId, client_id: 'mock-2', client_name: 'Jordan Kim', session_date: tomorrowStr, start_time: '10:00', duration_minutes: 60, notes: 'Lower body focus', status: 'scheduled' },
    { coach_id: coachId, client_id: 'mock-4', client_name: 'Casey Chen', session_date: dayAfterStr, start_time: '16:00', duration_minutes: 30, notes: 'Program check-in', status: 'scheduled' },
  ];

  for (const m of mocks) {
    await addSession(m);
  }
}

/** Get today's sessions for a coach */
export async function getTodaySessions(coachId: string): Promise<CoachSession[]> {
  const todayStr = formatDateStr(new Date());
  return getSessions(coachId, todayStr);
}
